// Visual tokens for the client-type badge in the Seller Portal pending-approval
// list. Values confirmed against Figma node 7033:19005 (pixel-sampled, cross-
// matched to the ui-kit CSS) and the backend API contract (clientType 1/2
// mapping). clientType selects the style; the label always comes from the
// API's clientTypeName, never from a hardcoded map (see getClientTypeVisual).

type ClientTypeBgClass = 'bg-primary-100' | 'bg-neutral-200';
type ClientTypeTextClass = 'text-primary-700' | 'text-neutral-700';

export interface ClientTypeVisual {
  bg: ClientTypeBgClass;
  border: 'border-neutrals-400';
  text: ClientTypeTextClass;
  // Brand scope for the badge element. The app runs under the UNACEM brand,
  // whose `primary-*` is red; the Progresol 2.0 segment keeps its blue chip by
  // re-scoping `primary-*` to Progresol on that one element.
  brand?: 'progresol';
}

export const ORDER_CLIENT_TYPE_VISUAL_MAP: Record<number, ClientTypeVisual> = {
  1: {
    bg: 'bg-neutral-200',
    border: 'border-neutrals-400',
    text: 'text-neutral-700',
  },
  2: {
    bg: 'bg-primary-100',
    border: 'border-neutrals-400',
    text: 'text-primary-700',
    brand: 'progresol',
  },
};

// Style for a clientType id outside the known map — the catalog is open
// (more segments may be provisioned without a frontend release), so an
// unmapped id still renders its clientTypeName, with this neutral style
// rather than being suppressed.
export const CLIENT_TYPE_VISUAL_FALLBACK: ClientTypeVisual = {
  bg: 'bg-neutral-200',
  border: 'border-neutrals-400',
  text: 'text-neutral-700',
};

// Badge label always comes from clientTypeName (the API's ready-to-paint
// text) — never re-derived from a local id→label map. clientType only
// selects the style; an id outside the known map still renders its
// clientTypeName, with the neutral fallback style instead of no badge.
export function resolveClientTypeVisual(
  clientType: number | null,
  clientTypeName: string | null,
): (ClientTypeVisual & { label: string }) | null {
  if (!clientTypeName) return null;
  const style =
    clientType != null
      ? (ORDER_CLIENT_TYPE_VISUAL_MAP[clientType] ??
        CLIENT_TYPE_VISUAL_FALLBACK)
      : CLIENT_TYPE_VISUAL_FALLBACK;
  return { label: clientTypeName, ...style };
}
